import type {
  Account,
  AllocationDimension,
  AllocationReport,
  BackfillOutcome,
  GapReport,
  Holding,
  Instrument,
  Me,
  Page,
  ProblemDetail,
  Snapshot,
  TokenResponse,
} from './types';

/**
 * Where the tokens live between page loads.
 *
 * <p>Abstracted because the two clients cannot use the same one: the browser has
 * `localStorage`, and React Native must use the Keychain or Keystore rather than AsyncStorage
 * (NFR-SEC2). Neither belongs in this package.
 */
export interface TokenStore {
  read(): StoredTokens | null;
  write(tokens: StoredTokens): void;
  clear(): void;
}

export interface StoredTokens {
  accessToken: string;
  refreshToken: string;
  /** Epoch milliseconds. Used only to refresh early, never trusted as authorisation. */
  expiresAt: number;
}

/** An API error carrying the problem+json body, so a caller can show what the server said. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetail;

  constructor(status: number, problem: ProblemDetail) {
    super(problem.detail ?? problem.title ?? `Request failed with ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** FR-D3 and FR-E4 both answer an unanswerable-but-well-formed request with a 422. */
  get isUnprocessable(): boolean {
    return this.status === 422;
  }
}

export interface ClientOptions {
  baseUrl: string;
  tokens: TokenStore;
  /** Called when the session is gone for good, so the UI can send the user to the login screen. */
  onSignedOut?: () => void;
}

/** Refresh this long before the access token actually expires, to survive a slow request. */
const REFRESH_MARGIN_MS = 60_000;

export class GreenCandleClient {
  private readonly baseUrl: string;
  private readonly tokens: TokenStore;
  private readonly onSignedOut?: () => void;

  /**
   * One refresh at a time. Several queries firing at once on a stale token would otherwise each
   * start their own refresh, and the backend rotates refresh tokens on use and revokes the whole
   * session when it sees one reused (FR-A1) - so the parallel refreshes would log the user out.
   */
  private refreshing: Promise<StoredTokens> | null = null;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.tokens = options.tokens;
    this.onSignedOut = options.onSignedOut;
  }

  // --------------------------------------------------------------------------------- auth

  async login(username: string, password: string): Promise<Me> {
    const tokens = await this.request<TokenResponse>('POST', '/api/auth/login', {
      body: { username, password },
      anonymous: true,
    });
    this.store(tokens);
    return this.me();
  }

  async logout(): Promise<void> {
    const stored = this.tokens.read();
    if (stored) {
      try {
        await this.request('POST', '/api/auth/logout', {
          body: { refreshToken: stored.refreshToken },
        });
      } catch {
        // A logout that fails server-side still ends the session on this device. Leaving the
        // tokens in place because the network blipped would be the worse outcome.
      }
    }
    this.tokens.clear();
  }

  me(): Promise<Me> {
    return this.request<Me>('GET', '/api/auth/me');
  }

  isSignedIn(): boolean {
    return this.tokens.read() !== null;
  }

  // ----------------------------------------------------------------------------- portfolio

  listAccounts(): Promise<Account[]> {
    return this.request<Account[]>('GET', '/api/accounts');
  }

  getAccount(id: string): Promise<Account> {
    return this.request<Account>('GET', `/api/accounts/${id}`);
  }

  /** FR-E1 with no date; FR-E2's reconstruction of a past day when one is given. */
  holdings(accountId: string, asOf?: string): Promise<Holding[]> {
    return this.request<Holding[]>('GET', `/api/accounts/${accountId}/holdings`, {
      query: { asOf },
    });
  }

  // ------------------------------------------------------------------------ FR-E2 snapshots

  snapshots(accountId: string, from?: string, to?: string): Promise<Snapshot[]> {
    return this.request<Snapshot[]>('GET', `/api/accounts/${accountId}/snapshots`, {
      query: { from, to },
    });
  }

  captureSnapshot(accountId: string, date?: string): Promise<Snapshot> {
    return this.request<Snapshot>('POST', `/api/accounts/${accountId}/snapshots`, {
      query: { date },
    });
  }

  snapshotGaps(accountId: string, from?: string, to?: string): Promise<GapReport> {
    return this.request<GapReport>('GET', `/api/accounts/${accountId}/snapshots/gaps`, {
      query: { from, to },
    });
  }

  fillSnapshotGaps(accountId: string, from?: string, to?: string): Promise<BackfillOutcome> {
    return this.request<BackfillOutcome>('POST', `/api/accounts/${accountId}/snapshots/fill-gaps`, {
      query: { from, to },
    });
  }

  backfillSnapshots(accountId: string, from?: string, to?: string): Promise<BackfillOutcome> {
    return this.request<BackfillOutcome>('POST', `/api/accounts/${accountId}/snapshots/backfill`, {
      query: { from, to },
    });
  }

  // ----------------------------------------------------------------------- FR-E4 allocation

  /**
   * Across every account, or one of them. `currency` is required only when the accounts in scope
   * do not share a base currency; without it the server answers 400 rather than picking one.
   */
  allocation(params: {
    dimension: AllocationDimension;
    accountId?: string;
    currency?: string;
    asOf?: string;
  }): Promise<AllocationReport> {
    return this.request<AllocationReport>('GET', '/api/portfolio/allocation', { query: params });
  }

  // ---------------------------------------------------------------------------- instruments

  instruments(assetClass?: string, page = 0, size = 50): Promise<Page<Instrument>> {
    return this.request<Page<Instrument>>('GET', '/api/instruments', {
      query: { assetClass, page, size },
    });
  }

  setSector(instrumentId: string, sector: string | null): Promise<Instrument> {
    return this.request<Instrument>('PATCH', `/api/instruments/${instrumentId}/sector`, {
      body: { sector },
    });
  }

  // -------------------------------------------------------------------------------- plumbing

  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown;
      query?: Record<string, string | number | undefined>;
      anonymous?: boolean;
      retryOnUnauthorized?: boolean;
    } = {},
  ): Promise<T> {
    // Built as a string rather than through `URL`, which requires an absolute address: the web
    // app passes an empty base so that requests are same-origin and Caddy routes them, and
    // `new URL('/api/...')` with no base throws. Mobile will pass an absolute base, and
    // concatenation is correct for both.
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    }
    const query = params.toString();
    const url = `${this.baseUrl}${path}${query ? `?${query}` : ''}`;

    const headers: Record<string, string> = { Accept: 'application/json' };
    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (!options.anonymous) {
      const token = await this.accessToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    const response = await fetch(url, {
      method,
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });

    // A 401 on a token we believed was valid means the server disagrees - it was revoked, or the
    // signing key changed. One refresh attempt, then give up rather than loop.
    if (response.status === 401 && !options.anonymous && options.retryOnUnauthorized !== false) {
      const refreshed = await this.refresh().catch(() => null);
      if (refreshed) {
        return this.request<T>(method, path, { ...options, retryOnUnauthorized: false });
      }
      this.signOut();
      throw new ApiError(401, { title: 'Session expired', detail: 'Please sign in again.' });
    }

    if (!response.ok) {
      throw new ApiError(response.status, await this.problem(response));
    }
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  }

  private async problem(response: Response): Promise<ProblemDetail> {
    try {
      return (await response.json()) as ProblemDetail;
    } catch {
      // A proxy or a crash can produce a non-JSON error. Say what is actually known.
      return { status: response.status, title: response.statusText || 'Request failed' };
    }
  }

  private async accessToken(): Promise<string | null> {
    const stored = this.tokens.read();
    if (!stored) {
      return null;
    }
    if (Date.now() < stored.expiresAt - REFRESH_MARGIN_MS) {
      return stored.accessToken;
    }
    const refreshed = await this.refresh().catch(() => null);
    return refreshed ? refreshed.accessToken : null;
  }

  private refresh(): Promise<StoredTokens> {
    if (this.refreshing) {
      return this.refreshing;
    }
    const stored = this.tokens.read();
    if (!stored) {
      return Promise.reject(new Error('Not signed in'));
    }

    this.refreshing = this.request<TokenResponse>('POST', '/api/auth/refresh', {
      body: { refreshToken: stored.refreshToken },
      anonymous: true,
    })
      .then((tokens) => this.store(tokens))
      .catch((error: unknown) => {
        // The refresh token is single-use and rotating; if it is rejected the session is over.
        this.signOut();
        throw error;
      })
      .finally(() => {
        this.refreshing = null;
      });

    return this.refreshing;
  }

  private store(tokens: TokenResponse): StoredTokens {
    const stored: StoredTokens = {
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + tokens.expiresInSeconds * 1000,
    };
    this.tokens.write(stored);
    return stored;
  }

  private signOut(): void {
    this.tokens.clear();
    this.onSignedOut?.();
  }
}
